import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/capacitor';
import { useShallow } from 'zustand/react/shallow';

import { TPaymentType } from '@mopla/data-models';

import { fetchUser } from '../../actions/userActions';
import { useBusinessLayer } from '../../business-logic';

import {
	createSepaPaymentMethodApi,
	deletePaymentMethodApi,
	fetchPaymentMethodApi,
	setPaymentMethodApi,
} from './paymentMethodApi';
import { usePaymentMethodStore } from './paymentMethodStore';
import {
	ICreateSepaPaymentMethodParams,
	ISetPaymentMethodParams,
	IUsePaymentMethodReturn,
} from './types';
import { isPaymentMethodValid } from './validatePaymentMethod';

/**
 * Hook for managing the user's payment method(s).
 */
export const usePaymentMethod = (): IUsePaymentMethodReturn => {
	const { dispatch } = useBusinessLayer();

	const {
		paymentMethod,
		isLoading,
		isSaving,
		setPaymentMethod,
		setIsLoading,
		setIsSaving,
	} = usePaymentMethodStore(
		useShallow((state) => ({
			paymentMethod: state.paymentMethod,
			isLoading: state.isLoading,
			isSaving: state.isSaving,
			setPaymentMethod: state.setPaymentMethod,
			setIsLoading: state.setIsLoading,
			setIsSaving: state.setIsSaving,
		}))
	);

	const effectivePaymentType: TPaymentType | undefined = useMemo(() => {
		if (!paymentMethod) return undefined;

		if (
			paymentMethod.type === 'card' &&
			paymentMethod.card?.wallet?.type === 'apple_pay'
		) {
			return 'apple_pay';
		}

		if (
			paymentMethod.type === 'card' &&
			paymentMethod.card?.wallet?.type === 'google_pay'
		) {
			return 'google_pay';
		}

		return paymentMethod.type;
	}, [paymentMethod]);

	const isValid = useMemo(
		() => isPaymentMethodValid(paymentMethod),
		[paymentMethod]
	);

	/** Fetch payment method with state management */
	const fetchPaymentMethod = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await fetchPaymentMethodApi();
			setPaymentMethod(result);
		} catch {
			/** Error already logged in API function */
		} finally {
			setIsLoading(false);
		}
	}, [setIsLoading, setPaymentMethod]);

	/** Delete payment method with state management */
	const deletePaymentMethod = useCallback(async () => {
		setIsSaving(true);
		try {
			await deletePaymentMethodApi();
			setPaymentMethod(null);
		} catch {
			/** Error already logged in API function */
		} finally {
			setIsSaving(false);
		}
	}, [setIsSaving, setPaymentMethod]);

	const setDefaultPaymentMethod = useCallback(
		async (params: ISetPaymentMethodParams) => {
			setIsSaving(true);

			try {
				await setPaymentMethodApi(params);
			} catch {
				/** Error already logged in API function */
			} finally {
				setIsSaving(false);
			}

			/** Dispatch fetchUser to refresh user data (should contain `hasDefaultStripePaymentMethod` field) */
			dispatch(fetchUser());

			/** Refetch to get the latest payment method state */
			await fetchPaymentMethod();
		},
		[dispatch, setIsSaving, fetchPaymentMethod]
	);

	/** Refresh user data and the default payment method after a server-side change (e.g. Pay by Bank setup). */
	const syncPaymentMethod = useCallback(async () => {
		dispatch(fetchUser());
		await fetchPaymentMethod();
	}, [dispatch, fetchPaymentMethod]);

	/** Basically this proxies the call to the `setDefaultPaymentMethod` handler,
	 * but later we can support having multiple PMs for a user, that's why we will have both `setDefault...` and `add...` handlers.
	 * */
	const addStripePMHandler = useCallback(
		async (params: ISetPaymentMethodParams) => {
			if (!params.paymentMethodId && !params.setupIntentId) {
				return;
			}

			await setDefaultPaymentMethod(params);

			//TODO fix the sentry call
			Sentry.addBreadcrumb({
				category: 'payment',
				message: 'process payment method on native',
				data: params,
			});
		},
		[setDefaultPaymentMethod]
	);

	/** Create SEPA payment method with state management */
	const createSepaPaymentMethod = useCallback(
		async (params: ICreateSepaPaymentMethodParams) => {
			setIsSaving(true);

			try {
				await createSepaPaymentMethodApi(params);

				/** Dispatch fetchUser to refresh user data (should contain `hasDefaultStripePaymentMethod` field) */
				dispatch(fetchUser());

				/** Refetch to get the latest payment method state */
				await fetchPaymentMethod();
			} finally {
				setIsSaving(false);
			}
		},
		[setIsSaving, dispatch, fetchPaymentMethod]
	);

	return {
		defaultPaymentMethod: paymentMethod,
		effectivePaymentType,
		isPaymentMethodLoading: isLoading,
		isPaymentMethodSaving: isSaving,
		isPaymentMethodValid: isValid,
		refetchPaymentMethod: fetchPaymentMethod,
		removePaymentMethod: deletePaymentMethod,
		setDefaultPaymentMethod,
		syncPaymentMethod,
		addStripePMHandler,
		createSepaPaymentMethod,
	};
};
